/*
 * @Author: Ryan
 * @Date: 2025-12-25
 * @Description: AI Avatar features section showcasing key features with alternating layout
 */
import Image from 'next/image';
import React from 'react';

import StartButton from './StartButton';

interface FeatureItemProps {
  title: string;
  description: string;
  imageUrl: string;
  imageAlt: string;
  reverse?: boolean;
  buttonText?: string;
  buttonHref?: string;
}

const FeatureItem: React.FC<FeatureItemProps> = ({
  title,
  description,
  imageUrl,
  imageAlt,
  reverse = false,
  buttonText = 'Create Your AI Avatar Video',
  buttonHref,
}) => {
  return (
    <div
      className={`flex flex-col ${reverse ? 'sm:flex-row-reverse' : 'sm:flex-row'} items-center gap-6 pad:gap-8 sm:gap-12`}
    >
      {/* Image */}
      <div className="flex-1 w-full">
        <div className="rounded-2xl overflow-hidden">
          <Image
            src={imageUrl}
            alt={imageAlt}
            width={540}
            height={360}
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 w-full flex flex-col items-start">
        <h3 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-gray-900 mb-3 pad:mb-4">
          {title}
        </h3>
        <p className="text-sm pad:text-base sm:text-base text-gray-600 mb-4 pad:mb-6 leading-relaxed">
          {description}
        </p>
        <StartButton text={buttonText} href={buttonHref} />
      </div>
    </div>
  );
};

const PowerfulFeaturesSection: React.FC = () => {
  const features = [
    {
      title: 'Choose from 100+ Realistic AI Presenter',
      description:
        'FalcoCut provides a wide selection of over 100 realistic AI presenters who look, speak, and act like real people. From professional business spokespeople and educators to casual influencers and friendly customer service reps, you can instantly select a presenter that fits your content. Each AI presenter delivers scripts with natural expressions, gestures, and tone, helping you produce engaging videos for product demos, training courses, or social media campaigns—without the need to hire on-camera talent.',
      imageUrl:
        'https://assets.falcocut.ai/landing-page/ai-avatar-generator/ai-avatar-100-realistic-presenters.png',
      imageAlt: 'Over 100 realistic AI presenters for video content',
    },
    {
      title: 'Custom AI Avatars for Your Brand',
      description:
        "Build avatars that represent your brand's unique identity. Whether you need a polished executive for corporate presentations, a charismatic influencer-style spokesperson, or your own digital twin, FalcoCut makes it possible. Upload a photo, record a short clip, or generate one from text prompts—then customize looks, clothing, and backgrounds. These avatars give your campaigns consistent branding across platforms, making them perfect for storytelling, promotional content, or internal communications.",
      imageUrl:
        'https://assets.falcocut.ai/landing-page/ai-avatar-generator/ai-avatar-custom-brand-avatars.png',
      imageAlt: 'Custom AI avatars tailored to your brand identity',
      buttonText: 'Generate Your AI Avatar',
      buttonHref: '/ai-image-generator',
    },
    {
      title: 'Photo to Talking Video',
      description:
        "Transform any static image into a dynamic speaking avatar. With FalcoCut's AI-driven lip-sync, facial expressions, and gestures, photos come to life in seconds. This is ideal for repurposing headshots into explainer videos, animating historical portraits for educational use, or quickly producing engaging content for social media without filming. Add narration, subtitles, or dubbing to make every frame informative and entertaining.",
      imageUrl:
        'https://assets.falcocut.ai/landing-page/ai-avatar-generator/ai-avatar-photo-to-talking-video.png',
      imageAlt: 'Transform photos into talking avatar videos',
    },
    {
      title: 'Create Your Digital Twin',
      description:
        "Film yourself once and let FalcoCut create a lifelike digital replica that mirrors your expressions, gestures, and speaking style. With your digital twin, you'll never need to appear on camera again—just upload scripts, and your avatar delivers them naturally in any language. Perfect for trainers, educators, and professionals who need scalable content for e-learning, onboarding, or product walkthroughs. Your digital twin ensures a consistent and authentic presence, 24/7.",
      imageUrl:
        'https://assets.falcocut.ai/landing-page/ai-avatar-generator/ai-avatar-digital-twin.png',
      imageAlt: 'Create your own AI digital twin avatar',
    },
    {
      title: 'Multilingual Voice',
      description:
        "Reach a global audience with powerful multilingual translation and voice capabilities. Easily translate your content into 30+ languages and accents, ensuring your message stays clear, natural, and culturally accurate across regions. Choose from FalcoCut's AI voice library or use voice cloning to maintain a consistent speaking style while delivering fluent translations in multiple languages. This makes it simple to localize videos, scale international marketing campaigns, and create multilingual training or educational content—without losing the original tone or intent of your message.",
      imageUrl:
        'https://assets.falcocut.ai/landing-page/ai-avatar-generator/ai-avatar-multilingual-voice.png',
      imageAlt: 'Multilingual AI voice support for global audiences',
    },
    {
      title: 'Access 100+ Stock Voices & Clone Your Own Voice',
      description:
        "Bring your avatar to life with a wide variety of natural-sounding voices. Choose from over 100 options across different tones, accents, and styles to match your audience. Whether you need a formal narrator, a friendly teacher, or a casual influencer, you'll always find the perfect voice to make your avatar sound authentic and engaging.",
      imageUrl:
        'https://assets.falcocut.ai/landing-page/ai-avatar-generator/ai-avatar-voice-cloning.png',
      imageAlt: 'AI voice cloning and 100+ stock voices',
    },
    {
      title: 'UGC & Social Media Avatars',
      description:
        'Produce eye-catching, influencer-style avatar videos designed for TikTok, Instagram Reels, or YouTube Shorts. With customizable outfits, settings, and tones, FalcoCut avatars adapt to match your brand personality or trending topics. No need to hire talent or manage complex shoots—you can scale user-generated content (UGC), social ads, and viral campaigns quickly and affordably. These avatars deliver relatable, scroll-stopping content that keeps your audience engaged across all social platforms.',
      imageUrl:
        'https://assets.falcocut.ai/landing-page/ai-avatar-generator/ai-avatar-ugc-social-media.png',
      imageAlt: 'UGC and social media AI avatar videos',
    },
    {
      title: 'Product Showcase',
      description:
        'Turn simple product shots into professional marketing assets. Upload your product photo and instantly generate polished lifestyle visuals. You can place products in realistic environments, add avatars to interact with them, or describe custom scenes with text prompts. Perfect for creating high-quality images for e-commerce listings, social media campaigns, and ad creatives—without expensive photo shoots.',
      imageUrl:
        'https://assets.falcocut.ai/landing-page/ai-avatar-generator/ai-avatar-product-showcase.png',
      imageAlt: 'AI avatar product showcase for e-commerce',
      buttonText: 'Create Your Product Avatar Video',
      buttonHref: '/ai-product-avatar',
    },
  ];

  return (
    <section className="w-full bg-white py-[90px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-3 pad:mb-4">
          AI Avatar Features for Real-World Content
        </h2>

        {/* Description */}
        <p className="text-sm pad:text-base text-center text-gray-600 max-w-4xl mx-auto mb-6 pad:mb-8">
          FalcoCut combines AI avatars, dubbing, subtitles, and video editing in one
          platform, helping you scale content creation faster and at lower cost.
        </p>

        <div className="space-y-12 pad:space-y-16 sm:space-y-24">
          {features.map((feature, index) => (
            <FeatureItem
              key={index}
              title={feature.title}
              description={feature.description}
              imageUrl={feature.imageUrl}
              imageAlt={feature.imageAlt}
              reverse={index % 2 === 1}
              buttonText={feature.buttonText}
              buttonHref={feature.buttonHref}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default PowerfulFeaturesSection;
